import { EventEmitter, Injectable } from '@angular/core';
import { CompanyStorage } from 'src/app/_core/company-storage';

export class ICustomer {
  id: number;
  birthday: string;
  firstName: string;
  genderId: string;
  lastName: string;
  patronymic: string;
  phone: string;
  isLastRequested?: boolean = false;
  bonusesAccount?: number = 0;
  percentDiscount?: number = 0;
  loyaltyDiscount?: number = 0;
  loyaltyTypeId?: number = 0;
  loyaltyName?: string;
  loyaltyPrograms?: any[];
  loyaltyProgramId?: number;
  customeCode?: number;
  refreshing?: boolean;
}

const CUSTOMERS_TABLE_KEY = '__customers';

@Injectable({ providedIn: 'root' })
export class CustomersRepository {
  onUpdated = new EventEmitter<ICustomer>();
  private _cacheByPhone: { [phone: string]: ICustomer };
  private _cacheById: { [id: number]: ICustomer };

  constructor(private _companyStorage: CompanyStorage) {}

  async getCustomer(id: number): Promise<ICustomer> {
    const repo = await this._companyStorage.repository<ICustomer>(
      CUSTOMERS_TABLE_KEY
    );

    return repo.getItem(id, 'id');
  }

  async getCustomerByPhone(phone: string): Promise<ICustomer> {
    const customers = await this.getCustomers();

    return customers.find((_) => _.phone == phone);
  }

  async getByPhoneCache(phone: string): Promise<ICustomer> {
    const customersByPhone = await this.getCustomersCashedByPhone();
    return customersByPhone[phone];
  }

  async getByIdCache(id: number): Promise<ICustomer> {
    const customersById = await this._getCashById();
    return customersById[id];
  }

  async addCustomer(customer: ICustomer): Promise<void> {
    const repo = await this._companyStorage.repository<ICustomer>(
      CUSTOMERS_TABLE_KEY
    );
    const items = await repo.getItems();
    items.push(customer);
    await repo.loadItems(items);
    this._clearCache();
  }

  async updateCustomerBonuses(
    customer: ICustomer,
    bonuses: number
  ): Promise<void> {
    const repo = await this._companyStorage.repository<ICustomer>(
      CUSTOMERS_TABLE_KEY
    );

    const updatedCustomers = [];
    const _customers = await this.getCustomers();
    for (let c of _customers) {
      if (customer.id > 0 && c.id == customer.id) {
        c.bonusesAccount = bonuses;
        updatedCustomers.push(c);
      } else if (customer.phone && c.phone == customer.phone) {
        if (c.id > 0) {
          customer.id = c.id;
          c.bonusesAccount = bonuses;
          updatedCustomers.push(c);
        }
      } else {
        updatedCustomers.push(c);
      }
    }

    repo.loadItems(updatedCustomers);
    this._clearCache();
  }

  async updateCustomer(customer: ICustomer): Promise<void> {
    const repo = await this._companyStorage.repository<ICustomer>(
      CUSTOMERS_TABLE_KEY
    );

    const updatedCustomers = [];
    const _customers = await this.getCustomers();
    for (let c of _customers) {
      if (customer.id > 0 && c.id == customer.id) {
        updatedCustomers.push(customer);
      } else if (customer.phone && c.phone == customer.phone) {
        if (c.id > 0) {
          customer.id = c.id;
          updatedCustomers.push(customer);
        }
      } else {
        updatedCustomers.push(c);
      }
    }

    repo.loadItems(updatedCustomers);
    this._clearCache();
  }

  async updateCustomerByPhone(customer: ICustomer): Promise<void> {
    const repo = await this._companyStorage.repository<ICustomer>(
      CUSTOMERS_TABLE_KEY
    );

    const updatedCustomers = [];
    const _customers = await this.getCustomers();
    let _customerFound = false;
    for (let c of _customers) {
      if (customer.id > 0 && c.id == customer.id) {
        updatedCustomers.push(customer);
        _customerFound = true;
      } else if (customer.phone && c.phone == customer.phone) {
        _customerFound = true;

        if (c.id > 0) {
          customer.id = c.id;
        }
        updatedCustomers.push(customer);
      } else {
        updatedCustomers.push(c);
      }
    }

    if (!_customerFound) {
      throw `Клієнта не знайдено. ID ${customer.id}, Телефон ${customer.phone}`;
    }

    this.onUpdated.emit(customer);
    repo.loadItems(updatedCustomers);
    this._clearCache();
  }

  async getCustomers(): Promise<ICustomer[]> {
    const repo = await this._companyStorage.repository<ICustomer>(
      CUSTOMERS_TABLE_KEY
    );

    return repo.getItems();
  }

  private async getCustomersCashedByPhone(): Promise<{
    [phone: string]: ICustomer;
  }> {
    if (!this._cacheByPhone) {
      const repo = await this._companyStorage.repository<ICustomer>(
        CUSTOMERS_TABLE_KEY
      );
      const items = await repo.getItems();

      this._cacheByPhone = items.reduce((prev: any, current: ICustomer) => {
        prev[current.phone] = current;
        return prev;
      }, {});
    }

    return this._cacheByPhone;
  }

  private async _getCashById(): Promise<{ [id: number]: ICustomer }> {
    if (!this._cacheById) {
      const repo = await this._companyStorage.repository<ICustomer>(
        CUSTOMERS_TABLE_KEY
      );
      const items = await repo.getItems();

      this._cacheById = items.reduce((prev: any, current: ICustomer) => {
        prev[current.id] = current;
        return prev;
      }, {});
    }

    return this._cacheById;
  }

  async loadCustomers(customers: ICustomer[]) {
    const repo = await this._companyStorage.repository<ICustomer>(
      CUSTOMERS_TABLE_KEY
    );

    repo.loadItems(customers);
    this._clearCache();
  }

  private _clearCache() {
    this._cacheByPhone = null;
    this._cacheById = null;
  }

  async clearStorage() {
    const repo = await this._companyStorage.repository<ICustomer>(
      CUSTOMERS_TABLE_KEY
    );

    repo.deleteTable();
    this._clearCache();
  }
}
